import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { formatMoney } from "@/lib/currency";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { trackOnboarding } from "@/lib/onboardingTrack";

const ACTION_ROUTE = {
  review_subscription: "/subscriptions",
  reduce_category: "/budget",
  review_nonessentials: "/expenses",
  prepay_loan: "/loans",
  increase_contribution: "/goals",
};

export default function Optimizer() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    trackOnboarding("first_view_optimizer");
    (async () => { const r = await api.get("/optimizer/insights"); setData(r.data); })();
  }, []);

  const cur = user?.currency || "USD";
  if (!data) return <div className="text-sm text-[var(--text-muted)]">{t("pages.optimizer.analyzing")}</div>;

  const TYPE_LABEL = {
    subscription: t("pages.optimizer.type_subscription"),
    expense: t("pages.optimizer.type_expense"),
    emi: t("pages.optimizer.type_emi"),
    goal: t("pages.optimizer.type_goal"),
  };
  const SEV_LABEL = {
    high: t("pages.optimizer.priority_high"),
    medium: t("pages.optimizer.priority_medium"),
    low: t("pages.optimizer.priority_low"),
  };

  const filtered = data.insights.filter(i => filter === "all" || i.type === filter);
  const bySeverity = { high: [], medium: [], low: [] };
  filtered.forEach(i => bySeverity[i.severity]?.push(i));

  return (
    <div>
      <PageHeader subtitle={t("pages.optimizer.subtitle")} title={t("pages.optimizer.title")}
        action={<Link to="/advisor" className="btn-primary" data-testid="opt-ask-ai-btn"><Sparkles size={16}/> {t("pages.optimizer.chat_ai")}</Link>}/>

      <div className="grid md:grid-cols-3 gap-5 mb-6 stagger">
        <div className="card-surface p-6">
          <div className="label-eyebrow">{t("pages.optimizer.monthly_potential")}</div>
          <div className="num-hero mt-3 text-[var(--accent-positive)]">{formatMoney(data.total_monthly_savings, cur)}</div>
          <div className="text-sm text-[var(--text-muted)] mt-1">{t("pages.optimizer.across_recs", { count: data.count })}</div>
        </div>
        <div className="card-surface p-6">
          <div className="label-eyebrow">{t("pages.optimizer.yearly_potential")}</div>
          <div className="num-hero mt-3">{formatMoney(data.total_yearly_savings, cur)}</div>
          <div className="text-sm text-[var(--text-muted)] mt-1">{t("pages.optimizer.if_all_taken")}</div>
        </div>
        <div className="card-surface p-6">
          <div className="label-eyebrow">{t("pages.optimizer.breakdown_l")}</div>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between"><span>{t("pages.optimizer.subscriptions_l")}</span><span className="num-md">{data.insights.filter(i => i.type === "subscription").length}</span></div>
            <div className="flex justify-between"><span>{t("pages.optimizer.expenses_l")}</span><span className="num-md">{data.insights.filter(i => i.type === "expense").length}</span></div>
            <div className="flex justify-between"><span>{t("pages.optimizer.loans_emis")}</span><span className="num-md">{data.insights.filter(i => i.type === "emi").length}</span></div>
            <div className="flex justify-between"><span>{t("pages.optimizer.goals_l")}</span><span className="num-md">{data.insights.filter(i => i.type === "goal").length}</span></div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {["all","subscription","expense","emi","goal"].map(tp => (
          <button key={tp} onClick={() => setFilter(tp)}
            className={`badge ${filter===tp?"badge-success":"badge-neutral"}`} data-testid={`opt-filter-${tp}`}>
            {tp === "all" ? t("pages.optimizer.all_f") : TYPE_LABEL[tp]}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState title={t("pages.optimizer.optimized_title")} body={t("pages.optimizer.optimized_body")}/>
      ) : (
        <div className="space-y-6">
          {["high","medium","low"].map(sev => bySeverity[sev].length > 0 && (
            <div key={sev}>
              <div className="label-eyebrow mb-3">{SEV_LABEL[sev]}</div>
              <div className="grid md:grid-cols-2 gap-4 stagger">
                {bySeverity[sev].map(i => (
                  <div key={i.id} className="card-flat p-5" data-testid={`insight-${i.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`badge ${sev==="high"?"badge-danger":sev==="medium"?"badge-warn":"badge-neutral"}`}>{TYPE_LABEL[i.type]}</span>
                        </div>
                        <div className="mt-2 font-medium text-base" style={{fontFamily:'var(--font-heading)'}}>{i.title}</div>
                        <div className="text-sm text-[var(--text-secondary)] mt-1.5 leading-relaxed">{i.detail}</div>
                      </div>
                      {i.monthly_savings > 0 && (
                        <div className="text-right shrink-0">
                          <div className="num-md text-[var(--accent-positive)]">{formatMoney(i.monthly_savings, cur)}</div>
                          <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">{t("pages.optimizer.per_month")}</div>
                        </div>
                      )}
                    </div>
                    <div className="mt-4">
                      <Link to={ACTION_ROUTE[i.action] || "/dashboard"} className="btn-primary text-sm" data-testid={`action-${i.id}`}>
                        {t("pages.optimizer.take_action")} <ArrowRight size={14}/>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
